import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { Loader2, CheckCircle2, XCircle, Layers, ArrowRight, Sparkles } from 'lucide-react';
import { Button } from '../../components/ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const TOKEN_KEY = 'sip_token';
const POLL_MS = 3000;

/**
 * BoscoCaptureKitProgress — Phase 4 tail-end screen.
 *
 * Reads the fan-out plan returned by /generate-kit (passed via
 * `location.state.plan`) or, as a fallback, fetches the persisted kit
 * doc from GET /kit/{kitId}. Then polls
 * /api/content-engine/generate/job/{jobId} for each item until every
 * job is done or failed.
 */
export default function BoscoCaptureKitProgress() {
  const { kitId } = useParams();
  const navigate = useNavigate();
  const { state } = useLocation();
  const [jobs, setJobs] = useState(state?.plan?.jobs || null);
  const [meta] = useState(state?.plan || null);
  const [error, setError] = useState(null);
  const pollRef = useRef(null);

  // If the user reloaded, re-hydrate the kit from the backend.
  useEffect(() => {
    if (jobs) return;
    const token = localStorage.getItem(TOKEN_KEY) || '';
    (async () => {
      try {
        const r = await fetch(`${API_URL}/api/bosco-capture/kit/${kitId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!r.ok) throw new Error(`Could not load kit (${r.status})`);
        const d = await r.json();
        setJobs(d.jobs || []);
      } catch (e) {
        setError(e.message);
      }
    })();
  }, [kitId, jobs]);

  // Poll every job that hasn't reached a terminal state.
  useEffect(() => {
    if (!jobs || !jobs.length) return;
    const token = localStorage.getItem(TOKEN_KEY) || '';
    const tick = async () => {
      const active = jobs.filter((j) => j.status !== 'done' && j.status !== 'failed');
      if (!active.length) { clearInterval(pollRef.current); return; }
      const updates = await Promise.allSettled(active.map(async (j) => {
        const r = await fetch(
          `${API_URL}/api/content-engine/generate/job/${encodeURIComponent(j.jobId)}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (!r.ok) return { jobId: j.jobId, status: j.status };
        const d = await r.json();
        return {
          jobId: j.jobId,
          status: d.status || j.status,
          assetId: (d.result && d.result.assetId) || j.assetId,
          error: d.error || null,
        };
      }));
      setJobs((prev) => prev.map((j) => {
        const u = updates.find((x) => x.status === 'fulfilled' && x.value.jobId === j.jobId);
        return u ? { ...j, ...u.value } : j;
      }));
    };
    tick();
    pollRef.current = setInterval(tick, POLL_MS);
    return () => clearInterval(pollRef.current);
  }, [jobs?.length]);

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-950 text-slate-100 p-6" data-testid="bosco-capture-kit-progress-error">
        <div className="text-center max-w-sm">
          <p className="text-sm text-rose-300">{error}</p>
          <Button className="mt-4" onClick={() => navigate('/teacher-assignments')}>Back</Button>
        </div>
      </div>
    );
  }

  if (!jobs) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-950 text-slate-100" data-testid="bosco-capture-kit-progress-loading">
        <Loader2 className="h-6 w-6 animate-spin text-emerald-400" />
      </div>
    );
  }

  const doneCount   = jobs.filter((j) => j.status === 'done').length;
  const failedCount = jobs.filter((j) => j.status === 'failed').length;
  const allSettled  = doneCount + failedCount === jobs.length;

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-950 via-slate-950 to-emerald-950/40 text-slate-100 pb-24"
         data-testid="bosco-capture-kit-progress">
      <div className="max-w-md mx-auto px-5 pt-6">
        <div className="flex items-center gap-2 text-emerald-300 text-[10px] uppercase tracking-[0.2em] font-semibold">
          <Layers className="h-3 w-3" /> BOSCO Capture · Kit progress
        </div>
        <h1 className="text-2xl font-bold mt-1">
          {allSettled ? 'Your teaching kit is ready.' : 'Generating your teaching kit…'}
        </h1>
        <p className="text-xs text-slate-400 mt-1">
          {allSettled
            ? `${doneCount} of ${jobs.length} resource${jobs.length === 1 ? '' : 's'} produced${failedCount ? ` · ${failedCount} failed` : ''}.`
            : `Running ${jobs.length} resource${jobs.length === 1 ? '' : 's'} through the Content Engine.`}
        </p>

        {meta && (
          <div className="mt-4 rounded-lg border border-white/10 bg-white/5 p-3 text-[11px] text-slate-400">
            <div className="grid grid-cols-2 gap-y-1">
              <div><span className="text-slate-500">Board</span> · {String(meta.board || '').toUpperCase() || '—'}</div>
              <div><span className="text-slate-500">Class</span> · {meta.class || '—'}</div>
              <div><span className="text-slate-500">Subject</span> · {meta.subject || '—'}</div>
              <div className="truncate"><span className="text-slate-500">Chapter</span> · {meta.chapter || '—'}</div>
            </div>
          </div>
        )}

        <ul className="mt-5 space-y-2" data-testid="bosco-capture-kit-progress-list">
          {jobs.map((j) => {
            const isDone = j.status === 'done';
            const isFailed = j.status === 'failed';
            return (
              <li key={j.jobId}
                  className="flex items-center justify-between rounded-lg border border-white/10 bg-white/5 px-3 py-2.5 text-sm"
                  data-testid={`bosco-capture-kit-progress-item-${j.itemId}`}>
                <div className="flex items-center gap-3 min-w-0">
                  {isDone
                    ? <CheckCircle2 className="h-4 w-4 text-emerald-400 flex-shrink-0" />
                    : isFailed
                      ? <XCircle className="h-4 w-4 text-rose-400 flex-shrink-0" />
                      : <Loader2 className="h-4 w-4 text-emerald-300 animate-spin flex-shrink-0" />}
                  <span className={`truncate ${isDone ? 'text-emerald-200' : isFailed ? 'text-rose-200' : 'text-slate-200'}`}>
                    {j.label}
                  </span>
                </div>
                <span className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">
                  {isDone ? 'done' : isFailed ? 'failed' : (j.status || 'queued')}
                </span>
              </li>
            );
          })}
        </ul>

        <div className="mt-6 space-y-2">
          <Button
            className="w-full bg-emerald-600 hover:bg-emerald-700 text-white"
            onClick={() => navigate('/teacher-assignments')}
            data-testid="bosco-capture-kit-progress-view-assignments"
          >
            {allSettled ? 'Open in Assignments' : 'View in Assignments'} <ArrowRight className="h-4 w-4 ml-1" />
          </Button>
          {allSettled && (
            <p className="text-[10px] text-emerald-300 text-center flex items-center justify-center gap-1">
              <Sparkles className="h-3 w-3" /> Resources are now in your Assignments library.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
